import React, { useContext, useLayoutEffect, useRef, useState } from 'react';

import './LabelPreview.scss';
import LabelFilterContext from '../LabelFilterContext/LabelFilterContext';
import { getIcon } from '@flybits/design-system';
import EmptyStateImage from 'assets/images/label-filter-preview-empty.png';
import ShowMoreImage from 'assets/images/label-filter-preview-more.png';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import ContentAPI from 'services/api/content.api';
import { useQuery } from '@tanstack/react-query';
import { Content } from 'interface/content/content.interface';
import useSettings from 'hooks/useSetting';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import errorImg from 'assets/images/error-img.svg';
import { OrderDirection } from 'components/Zones/v2/types';
import LabelPreviewContent from './LabelPreviewContent';

const MAIN_CLASS = 'label-preview';
const CLASSES = {
  DISCLAIMER: `${MAIN_CLASS}__disclaimer`,
  EMPTY: `${MAIN_CLASS}__empty`,
  SPINNER: `${MAIN_CLASS}__spinner`,
  TILE: `${MAIN_CLASS}__tile`,
  MORE_TILE: `${MAIN_CLASS}__tile--more`,
  TILE_CONTAINER: `${MAIN_CLASS}__tile__container`,
  TILE_LABEL: `${MAIN_CLASS}__tile__label`,
  TILE_HTML: `${MAIN_CLASS}__tile__html`,
  TILE_INFO: `${MAIN_CLASS}__tile__info`,
  TILE_INFO_TITLE: `${MAIN_CLASS}__tile__info__title`,
};

const MAX_TILES = 6;

const ContentApi = new ContentAPI();

const EmptyState = () => {
  return (
    <div className={CLASSES.EMPTY}>
      <img alt="empty-state-img" src={EmptyStateImage} />
      <h3>No Content Available</h3>
      <p>
        No content available under this label. Add content to an active experience with the same label to view it here.
      </p>
    </div>
  );
};

const LoadingSpinner = () => {
  return (
    <div className={CLASSES.SPINNER}>
      <LoadingIcon />
    </div>
  );
};

const ContentPreviewTile = ({
  content,
  label = '',
  defaultLang = 'en',
  grandParentRect,
}: {
  content: Content;
  label?: string;
  defaultLang?: string;
  grandParentRect?: DOMRect | null;
}) => {
  return (
    <div className={CLASSES.TILE}>
      <img
        src={content?.iconUrl || errorImg}
        onError={(ev) => {
          (ev.target as Element).setAttribute('src', errorImg);
        }}
        className={`${content?.iconUrl && 'contain'}`}
        alt="content icon"
      />
      <div className={CLASSES.TILE_INFO}>
        <span className={CLASSES.TILE_INFO_TITLE}>{content?.localizations?.[defaultLang]?.name}</span>
        <LabelPreviewContent
          title={content?.localizations?.[defaultLang]?.name}
          createdDate={content?.createdAt}
          labels={content?.labels}
          contentId={content?.id}
          grandParentRect={grandParentRect!}
        />
      </div>
      <div className={CLASSES.TILE_LABEL}>{label}</div>
      {content?.renderType === 'html' && <div className={CLASSES.TILE_HTML}>{'</>'}</div>}
    </div>
  );
};

const ShowMoreTile = ({ difference }: { difference: number }) => {
  return (
    <div className={CLASSES.MORE_TILE}>
      <img alt="show more content icon" src={ShowMoreImage} />
      <span>
        +{difference} more related content{difference > 1 ? 's' : ''}
      </span>
    </div>
  );
};

const ContentPreviewTiles = ({
  contents,
  defaultLang,
  label,
}: {
  contents?: Content[];
  defaultLang?: string;
  label?: string;
}) => {
  const contentsToRender = contents?.slice(0, MAX_TILES);
  const showMore = contents && contents.length > MAX_TILES;

  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerRect(containerRef.current.getBoundingClientRect());
    }
  }, [containerRef, setContainerRect]);

  return (
    <div className={CLASSES.TILE_CONTAINER} ref={containerRef}>
      {contentsToRender?.map((content) => (
        <ContentPreviewTile
          key={`label-preview-content-${content?.id}`}
          content={content}
          defaultLang={defaultLang}
          label={label}
          grandParentRect={containerRect}
        />
      ))}
      {showMore && <ShowMoreTile difference={contents.length - MAX_TILES} />}
    </div>
  );
};

const LabelFilterPreview = () => {
  const { selectedLabelFilter } = useContext(LabelFilterContext);
  const { languages, isInitialized: isLanguageInit } = useSettings();

  const defaultLang = getDefaultLanguage(languages);
  const {
    data: contents,
    isLoading,
    isFetching,
  } = useQuery({
    enabled: !!selectedLabelFilter,
    refetchOnWindowFocus: false,
    retry: false,
    queryKey: [
      'active-contents',
      selectedLabelFilter?.label,
      selectedLabelFilter?.limit,
      selectedLabelFilter?.orderBy,
      selectedLabelFilter?.orderAsc,
    ],
    queryFn: () =>
      ContentApi.getActiveInstances({
        label: selectedLabelFilter?.label,
        limit: selectedLabelFilter?.limit,
        sortby: selectedLabelFilter?.orderBy,
        sortorder: selectedLabelFilter?.orderAsc ? OrderDirection.ASCENDING : OrderDirection.DESCENDING,
      }),
  });

  return (
    <div className={`${MAIN_CLASS}`}>
      <div className={CLASSES.DISCLAIMER}>
        {getIcon('infoFilled', {})}
        <span>Note: You will only be able to see content that are in an active experience here</span>
      </div>
      {selectedLabelFilter && (isLoading || isFetching || !isLanguageInit) ? (
        <LoadingSpinner />
      ) : !!contents?.data?.length ? (
        <ContentPreviewTiles contents={contents.data} defaultLang={defaultLang} label={selectedLabelFilter?.label} />
      ) : (
        <EmptyState />
      )}
    </div>
  );
};

export default LabelFilterPreview;
